import { Dialog, DialogContent, Button, Divider, Typography, Box, Stack, IconButton } from '@mui/material';
import DialogActionBar from '../../components/DialogActionBar';
import { useUser } from '../../../../hooks/useUser';
import {
  Operation,
  OperationRole,
  OperationAgencyOfficial,
  UpdateOperationOfficialData,
  updateOperationOfficials
} from '../../../../networkRequest/operationsService';
import { useState, useEffect } from 'react';
import SearchableAgencyOfficialDropdown from '@components/SearchableAgencyOfficialDropdown';
import Delete from '@mui/icons-material/Delete';
import { Add } from '@mui/icons-material';
import { OperationAgencyOfficialAttributes } from '@pages/Operations/CreateOperationPage';
import { OPERATION_AGENCY_OFFICIAL_ROLES } from '../../../../constants';

interface EditIncidentCommandersModalProps {
  open: boolean;
  onClose: () => void;
  operation: Operation;
  onOperationUpdate: (op: Operation) => void;
  operationRoles: OperationRole[];
  incidentCommander?: OperationAgencyOfficial;
  deputyIncidentCommanders: OperationAgencyOfficial[];
}

export default function EditIncidentCommandersModal(props: EditIncidentCommandersModalProps) {
  const {
    open,
    onClose,
    operation,
    onOperationUpdate,
    operationRoles,
    incidentCommander: currentIncidentCommander,
    deputyIncidentCommanders: currentDeputyIncidentCommanders
  } = props;
  const { user } = useUser();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [incidentCommander, setIncidentCommander] = useState<OperationAgencyOfficialAttributes>({
    official: {
      id: Date.now(),
      first_name: '',
      last_name: '',
    },
    assignment: null,
  });
  const [deputyIncidentCommanders, setDeputyIncidentCommanders] = useState<OperationAgencyOfficialAttributes[]>([]);

  const getEmptyDropdownAgencyOfficial = (): OperationAgencyOfficialAttributes => {
    return {
      official: {
        id: Date.now(),
        first_name: '',
        last_name: '',
      },
      assignment: null,
    };
  };

  // When the dialog opens, set the incident commanders from the props
  useEffect(
    () => {
      if (open) {
        if (currentIncidentCommander !== undefined) {
          setIncidentCommander({
            official: {
              id: currentIncidentCommander.agency_official.id,
              first_name: currentIncidentCommander.agency_official.first_name,
              last_name: currentIncidentCommander.agency_official.last_name
            },
            assignment: OPERATION_AGENCY_OFFICIAL_ROLES.INCIDENT_COMMANDER
          });
        }
        setDeputyIncidentCommanders(currentDeputyIncidentCommanders.map(oao => {
          return {
            official: {
              id: oao.agency_official.id,
              first_name: oao.agency_official.first_name,
              last_name: oao.agency_official.last_name
            },
            assignment: OPERATION_AGENCY_OFFICIAL_ROLES.INCIDENT_COMMANDER
          }
        }));
      }
    },
    [open]
  );

  const handleAddDeputy = () => {
    setDeputyIncidentCommanders((prevDeputyIncidentCommanders) => {
      // Only allow one empty search input to be added to the end of the list
      if (
        prevDeputyIncidentCommanders.length > 0 &&
        prevDeputyIncidentCommanders[prevDeputyIncidentCommanders.length - 1].official?.id === undefined
      ) {
        return prevDeputyIncidentCommanders;
      }
      return [...prevDeputyIncidentCommanders, getEmptyDropdownAgencyOfficial()];
    });
  };

  const getRoleIdForAssignment = (assignment: string): string => {
    const match = operationRoles.find(
      (role) => role.name.trim().toLowerCase() === assignment.trim().toLowerCase()
    );
    return match ? match.id : '';
  };

  const handleDeputyIncidentCommandersChange = (id: number, official: OperationAgencyOfficialAttributes | null) => {
    setDeputyIncidentCommanders((prev) =>
      prev.map((agent) => {
        return agent?.official?.id === id
          ? { ...agent, ...official }
          : agent
      })
    );
  };

  // TO DO: Perform validation
  const isFormValid = () => true

  const handleSave = async () => {
    if (!user?.accessToken || !operation?.id) return;
    if (!isFormValid()) {
      setError(
        'Please select exactly one Incident Commander and do not assign the same official to multiple roles.'
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let operation_agency_officials: UpdateOperationOfficialData[] = []

      if (incidentCommander && incidentCommander.official?.id !== undefined) {
        operation_agency_officials.push({
          agency_official_id: incidentCommander.official?.id,
          operation_role_id: getRoleIdForAssignment('Incident Commander'),
          assignment: 'Incident Commander'
        });
      }
      for (const dic of deputyIncidentCommanders) {
        if (dic.official?.id === undefined) {
          continue;
        }
        operation_agency_officials.push({
          agency_official_id: dic.official?.id,
          operation_role_id: getRoleIdForAssignment('Deputy Incident Commander'),
          assignment: 'Deputy Incident Commander'
        });
      }

      // Compare incident commander and deputy incident commanders to the
      // original data and create updates to change their role and assignment
      const newAgencyOfficialIds = new Set<number>(operation_agency_officials.map(a => a.agency_official_id));
      const currentAgencyOfficialIds: number[] = [];
      if (currentIncidentCommander !== undefined) {
        currentAgencyOfficialIds.push(currentIncidentCommander.agency_official.id);
      }
      for (const dic of currentDeputyIncidentCommanders) {
        currentAgencyOfficialIds.push(dic.agency_official.id);
      }
      const removedAgencyOfficialIds: number[] = [];
      for (const id of currentAgencyOfficialIds) {
        if (!newAgencyOfficialIds.has(id)) {
          removedAgencyOfficialIds.push(id);
        }
      }

      for (const id of removedAgencyOfficialIds) {
        operation_agency_officials.push({
          agency_official_id: id,
          operation_role_id: getRoleIdForAssignment('Participant'),
          assignment: 'Participant'
        });
      }

      let updateData: UpdateOperationOfficialData[] = operation_agency_officials


      const result = await updateOperationOfficials({
        accessToken: user.accessToken,
        operationId: operation.id,
        operationOfficialData: updateData
      });

      if (result.error) {
        setError(result.error);
        return;
      }

      if (result.data) {
        onOperationUpdate(result.data);
      }

      onClose();
    }
    catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update incident commanders');
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          width: '783px',
          maxWidth: '783px',
          height: 'auto',
          maxHeight: '90vh',
          pt: 4,
          px: 1,
          pb: 1
        }
      }}>
      <DialogContent sx={{ p: 0, mb: 0 }}>
        <Typography variant="h5" sx={{ mb: 3, ml: 2 }}>
          Edit Incident Commanders
        </Typography>
        <Box sx={{ background: '#fff', borderRadius: 1, p: 2, mb: 2, pr: 7 }}>
          <SearchableAgencyOfficialDropdown
            label="Incident Commander"
            accessToken={user?.accessToken || ''}
            value={incidentCommander?.official}
            onSelect={official => setIncidentCommander({ official, assignment: OPERATION_AGENCY_OFFICIAL_ROLES.INCIDENT_COMMANDER })}
            onError={error => { throw new Error(error) }}
          />
        </Box>
        <Divider sx={{ mx: 2, my: 0 }} />
        <Box sx={{ background: '#fff', borderRadius: 1, p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            Deputy Incident Commanders
          </Typography>
          <Stack spacing={2}>
            {deputyIncidentCommanders.map((deputyIncidentCommander, index) => {
              return (
                <Stack
                  key={index}
                  direction="row"
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <SearchableAgencyOfficialDropdown
                    label="Deputy Incident Commander"
                    accessToken={user?.accessToken || ''}
                    value={deputyIncidentCommander.official}
                    onSelect={(official) => {
                      if (!official || !deputyIncidentCommander.official?.id) return;
                      handleDeputyIncidentCommandersChange(deputyIncidentCommander?.official.id, {
                        official: {
                          id: official?.id,
                          first_name: official.first_name,
                          last_name: official.last_name
                        },
                        assignment: OPERATION_AGENCY_OFFICIAL_ROLES.INCIDENT_COMMANDER
                      })
                    }}
                    onError={error => { throw new Error(error) }}
                  />
                  <IconButton
                    onClick={() => {
                      setDeputyIncidentCommanders((previousDeputyIncidentCommanders) => {
                        return previousDeputyIncidentCommanders.filter((_, prevIndex) => {
                          return (prevIndex !== index);
                        });
                      });
                    }}
                  >
                    <Delete sx={{ color: (theme) => theme.palette.utilityIcon }} />
                  </IconButton>
                </Stack>
              );
            })}

            <Box>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<Add />}
                onClick={handleAddDeputy}
              >
                Commander
              </Button>
            </Box>

          </Stack>
        </Box>
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActionBar sx={{ paddingRight: 1, pb: 1 }}>
        <Button onClick={onClose} variant="outlined" size="medium">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="medium"
          disabled={loading || !isFormValid()}>
          {loading ? 'Saving...' : 'Continue'}
        </Button>
      </DialogActionBar>
    </Dialog>
  );
}
